<template>
  <div :class="[$style.widget, 'hide-on-print']">
    <div :class="$style.dynamic">
      <div
        :class="{
          [$style.info]: true,
          [$style.info_is24]: isPlatformIs24(),
        }"
      >
        <p>
          {{ t('integrations.ubsMortgage.ubsWidget.info') }}
        </p>
      </div>
      <div :class="$style.button">
        <PartnerTrackedLink
          theme="secondary-bordered"
          :href="ubsUrl"
          target="_blank"
          rel="noopener"
          @click="trackLinkClick"
        >
          {{ t('integrations.ubsMortgage.ubsWidget.actionLabel') }}
        </PartnerTrackedLink>
      </div>
    </div>
    <div :class="$style.stable">
      <UBSLogo :class="$style.logo" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { computed, defineAsyncComponent } from 'vue';

import PartnerTrackedLink from '@/components/PartnerIntegrations/PartnerTrackedLink/PartnerTrackedLink.vue';
import { InteractionGoal } from '@/helpers/GTM/GTMParameters';
import { isPlatformIs24 } from '@/helpers/platformDetection';

import { useI18n } from 'vue-i18n';
import { trackPartnerLink } from '../functions/tracking';
import { getCmbFormUrlsByLocale } from './cmb-form-links';
import { CMBLandingPageSources } from './types';

defineOptions({
  name: 'UbsWidget',
});

const { t, locale } = useI18n();

interface Props {
  listing: Listing;
}
const props = defineProps<Props>();

const UBSLogo = defineAsyncComponent(() => import('./assets/ubs_logo.svg?component'));

const ubsUrl = computed((): string => {
  const cmbFormUrlsByLocale = getCmbFormUrlsByLocale();

  const url = new URL(cmbFormUrlsByLocale[locale.value]);
  url.searchParams.append('listingId', props.listing.id as string);
  url.searchParams.append('source', CMBLandingPageSources.PDP_TOP_WIDGET);
  return url.href;
});

const trackLinkClick = (): void => {
  trackPartnerLink(ubsUrl.value, InteractionGoal.MORTGAGE_CALCULATOR_LITE, props.listing.id);
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

$partner-widget-logo-height: 32px;
$partner-widget-min-height: 64px;

.widget {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-height: $partner-widget-min-height;
  padding-bottom: $space-200;
  border-bottom: 1px solid $color-grey-50;

  .dynamic,
  .stable {
    display: flex;
    flex-direction: column;
  }

  .dynamic {
    align-items: flex-start;
    justify-content: center;
  }

  .stable {
    align-items: flex-start;
    justify-content: space-between;
    margin-left: $space-150;

    .logo {
      height: $partner-widget-logo-height;
    }
  }

  .info {
    @include heading-4;

    &.info_is24 {
      @include heading-5($color-grey-100);
      @include font-bold;
    }

    > p {
      padding: 0;
      margin: 0;
    }
  }

  .button {
    display: flex;
    align-items: center;
    margin-top: $space-75;
  }

  // Mobile styles
  @media (max-width: $viewport-medium-min) {
    flex-direction: column-reverse;

    .info {
      @include heading-5;
    }

    .stable {
      margin-bottom: $space-75;
      margin-left: 0;
    }
  }
}
</style>
